/**
 * FilteredCount - Query result count with trend
 *
 * Usage:
 *   <FilteredCount
 *     data={{
 *       count: 47,
 *       query: 'emails containing "schedule call"',
 *       trend: 15,
 *       previousCount: 41
 *     }}
 *   />
 */

import type { BlockProps, FilteredCountData } from './types';
import { getTrendColor } from './colors';
import './blocks.css';

export type FilteredCountProps = BlockProps<FilteredCountData>;

export function FilteredCount({
  data,
  title,
  className,
}: FilteredCountProps) {
  const { count: rawCount, query, trend, previousCount } = data;

  // Guard against undefined/null values from LLM-generated card data
  const count = rawCount ?? 0;

  return (
    <div className={`filtered-count ${className || ''}`}>
      {title && (
        <div style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 'var(--space-2)',
        }}>
          {title}
        </div>
      )}

      <div className="filtered-count-value">
        {count.toLocaleString()}
      </div>

      <div className="filtered-count-query">
        {query}
      </div>

      {(trend !== undefined || previousCount !== undefined) && (
        <div
          className="filtered-count-trend"
          style={{ color: getTrendColor(trend || 0) }}
        >
          {trend !== undefined && (
            <>
              {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'}
              {Math.abs(trend)}%
            </>
          )}
          {previousCount !== undefined && (
            <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: '8px' }}>
              (was {previousCount.toLocaleString()})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default FilteredCount;
